import { Component, NgZone, OnDestroy, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Organization } from '../../../domain/organization/dto/Organization';
import { firstValueFrom, from, fromEvent, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { HorNavbarService } from '../../service/hor-navbar.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { CabinetService } from '../../../domain/cabinet/service/cabinet.service';
import {
  OrganizationAccesses,
  OrganizationsService,
} from '../../../domain/organization/components/service/organizations.service';
import { URL_PRESET } from './service/url-preset';
import { AuthService, UserType } from 'src/app/domain/auth/service/auth.service';
import {
  EmployeeRole,
  InitDataUserAccess,
  OrganizationWithAccesses,
} from 'src/app/core/component/hornavbar/dto/InitDataDTO';
import { Fund } from 'src/app/domain/fund-performance/dto/Fund';
import { HorNavBarShowElements, HorNavBarShowPicker } from './dto/HorNavBarShowElements';
import { PeriodService } from 'src/app/core/service/period.service';
import { URLToHeaderName } from './service/URLToHeaderName';
import { FundPerformance } from '../../../domain/fund-performance/dto/FundPerformance';
import { CookieService } from 'ngx-cookie-service';
import { ERROR_IMAGE_LINK } from '../../../domain/cabinet/component/cabinet/cabinet.component';
import { FundPerformanceBackendService } from '../../../domain/fund-performance/service/fund-performance-backend.service';
import { InitStateService } from 'src/app/core/service/init-state.service';
import { ShowUIService } from '../../service/show-ui.service';
import { CompanySearchDto } from '../../../domain/recovery/component/recovery-company/dto/CompanyWithOrgDto';
import { ToolTipSettings } from '../../../domain/shared/component/tooltip/tooltip.util';
import { MarketAnalysisInternalService } from 'src/app/domain/market-analysis/service/marketAnalysisInternalService';
import {
  UnsavedChangesModalService,
} from 'src/app/domain/shared/component/can-deactivate-guard/unsaved-changes-modal.service';
import { Profile } from '../../../domain/cabinet/dto/Profile';
import { cloneDeep } from 'lodash';
import { fadeInOutAnimation } from '../../../util/animations/fade-in-out.animation';
import { Location } from '@angular/common';
import {
  FinancialStatementService,
} from '../../../domain/financial-statement/services/common/financial-statement.service';
import { FsNavigationService } from '../../../domain/financial-statement/services/fs-nav/fs-navigation.service';
import { FsSelectorPeriod } from '../../../domain/financial-statement/utils/common/FsSelectorPeriod';
import {
  FsNavData,
  OwnershipEntityType,
} from '../../../domain/financial-statement/utils/common/utilityClasses';
import { RegExpService } from '../../../util/common/reg-exp.service';
import { FinancialStatement } from '../../../domain/financial-statement/models/common/FinancialStatement';
import { OrgPickerComponent, SelectedEntityAndOrg } from './org-picker/org-picker.component';
import { ManagementEntity } from '../../../domain/organization/dto/ManagementEntity';
import { FsGettersSettersService } from '../../../domain/financial-statement/services/common/fs-getters-setters.service';
import { MaHorNavData, MaSelector } from './market-analysis/ma-pojo/market-analysis-classes';
import { LocalDate } from '../../pojo/date-variable';
import { DateService } from '../../../util/common/date-service/date.service';
import { NavigationService } from '../../service/navigation.service';
import { PERIOD_PICKER_ACTION, PeriodPickerComponent } from './period-picker/period-picker.component';
import { SwitcherBoxType, SwitcherDateType, SwitcherNumberType } from "../numberswitcher/utils/number-switcher-common-classes";
import { BoxServiceService } from "../../../domain/box/service/box-service.service";
import { OrgUserManagementUrls } from "../../../domain/org-user-management/module/org-user-mngmnt-routing";
import { EntranceUrl } from "../../../domain/entrance/service/entrance.service";
import { CP_URLS } from '../../../domain/company-performance/dto/CP_URLS';
import { FP_URLS } from '../../../domain/fund-performance/component/fund-performances/pojo/FP_URLS';
import { AI_STRINGS } from "../../../domain/ai/utils";
import {
  NewPeriodEmitWrapper,
} from '../../../domain/fund-performance/component/shared/modal-copy-period/dto/PeriodToCopyDto';
import { NOTIFICATIONS, NotificationsService } from '../../service/notifications.service';
import { DATA_ROOM_URLS } from "../../../domain/box/component/data-room/data-room.util";
import { DEFAULT_DROP_DOWN_DELAY_TIME, DelayService } from "../../service/delay.service";

@Component({
  selector: 'app-horizontal-nav-bar',
  templateUrl: './horizontal-nav-bar.component.html',
  styleUrls: ['./horizontal-nav-bar.component.scss'],
  animations: [fadeInOutAnimation(200, 0)],
  providers: [DelayService],
})
export class HorizontalNavBarComponent implements OnInit, OnDestroy, AfterViewInit {
  protected readonly EntranceUrl = EntranceUrl;
  protected readonly AiStrings = AI_STRINGS;

  private _destroyStream: Subject<void> = new Subject<void>();
  private _fsDestroyStream: Subject<void> = new Subject<void>();

  /**
   * After the organization/fund change, a new {@var selectedOrganization} is fetched in subscription (ngOnInit).<br>
   * It triggers changes, incl. selected period {@see switchInterval}.<br> Inside one organization,
   * 1. if new selected fund has same period, same period should be chosen. <br>
   * 2. or Viewer/Admin access switched. <br>
   * 3. or period was copied, then new copied period should be chosen. <br>
   * Otherwise, the latest period must be chosen. <br>
   * This variable says, that saved before period must be chosen.<br>
   */
  private isSavedPeriodToBeSelected: boolean;

  @ViewChild('orgPicker') orgPicker: OrgPickerComponent;
  @ViewChild('periodPicker') periodPicker: PeriodPickerComponent;

  organizations: Organization[] = [];
  organizationsWithRoles: OrganizationWithAccesses[] = [];
  organizationsWithServiceAccess: OrganizationWithAccesses[];
  showUserManagementOptions = false;
  orgAccesses = new OrganizationAccesses();
  periods: Date[];
  selectedOrganization: Organization;
  currentPageHeader = '';
  showUI = true;
  marketAnalysis = new MaHorNavData();
  is = new IsHorNav();
  selectedPeriod: Date;
  selectedSingleOrgForm: FormGroup<{ selectedSingleOrg: FormControl }> = this._formBuilder.group({
    selectedSingleOrg: '',
  });

  filteredOrganizations: Observable<Organization[]>;

  currentPage: string;
  currentService: SOFTWARE_SERVICE;
  fundPerformances: FundPerformance[];
  filteredFundPerformances: FundPerformance[];
  selectedFundPerformance: FundPerformance;
  isDisableOrgSelectorAndShowButtonBack = false;

  /**
   * Which basic elements in the header should be shown. They change depending on the url.
   */
  showElements: HorNavBarShowElements = {
    showOrgFundPicker: true,
    periods: true,
    periodTypeSwitcher: false,
    numberSwitcher: false,
    showIrr: false,
    showPortfolio: false,
    singleOrg: false,
    market_analysis: false,
    vintagePeriods: false,
    adminSwitcher: true,
    search: true,
    notifications: true,
    logoSlider: true,
    allPeriodsButton: false,
    orcaAiButton: false,
  };

  isMultiAccess = false;
  userType: UserType = 'viewer';
  errorImageLink = ERROR_IMAGE_LINK;
  hasScrolledDown = false;

  toolTipSettings: ToolTipSettings = {
    show: false,
    title: '',
  };

  showPicker = new HorNavBarShowPicker();

  fsNavData: FsNavData = new FsNavData();

  boxType = SwitcherBoxType;
  showGlobalAiModal: boolean;
  protected readonly MaSelector = MaSelector;

  constructor(
    private authService: AuthService,
    private _formBuilder: FormBuilder,
    private navbarService: HorNavbarService,
    private router: Router,
    private cabinetService: CabinetService,
    private organizationsService: OrganizationsService,
    private route: ActivatedRoute,
    private periodService: PeriodService,
    private cookie: CookieService,
    private fundPerformanceService: FundPerformanceBackendService,
    private fsService: FinancialStatementService,
    private fsNavigationService: FsNavigationService,
    private fsGetterSetter: FsGettersSettersService,
    private initService: InitStateService,
    private showUIService: ShowUIService,
    private marketInternalService: MarketAnalysisInternalService,
    private unsavedChangesService: UnsavedChangesModalService,
    private ngZone: NgZone,
    private location: Location,
    private navigationService: NavigationService,
    private boxService: BoxServiceService,
    private notificationService: NotificationsService,
    private delayService: DelayService,
  ) {
    this.subscribeToScroll();

    router.events.subscribe((val) => {
      if (val instanceof NavigationEnd) {
        this.defineCurrentPageAndService();
        this.updateShowElements();
        window.scrollTo(0, 0);
      }
    });
  }

  ngOnInit(): void {
    this.showUIService.set(false);
    this.initSubscription();
    // todo extra?
    this.defineCurrentPageAndService();
    this.updateShowElements();
    this.getInitialData();
  }

  ngAfterViewInit(): void {
    this.delayService.initSubscribes(DEFAULT_DROP_DOWN_DELAY_TIME, this.showOrgDropDown, this.hideOrgDropDown);
  }

  ngOnDestroy(): void {
    this.organizations = [];
    this.selectedSingleOrgForm.reset();
    this._destroyStream.next();
  }

  resetOrgFundInputValue(): void {
    if (!this.orgPicker) {
      return;
    }
    this.orgPicker.selectedOrgForm.controls.selectedOrg.setValue('');
  }

  resetOrgInputValue(): void {
    this.selectedSingleOrgForm.controls.selectedSingleOrg.setValue('');
  }

  activateOrgPicker(): void {
    this.delayService.activate();
  }

  deactivateOrgPicker(): void {
    this.delayService.deactivate();
  }

  /**
   * Updates which ui elements are shown in HorNavBar such as org/fund picker, period picker and others
   * based on which page the user is on. currentPage holds the url value of the current page allowing us
   * to compare it with the URL_PRESET values which dictate the elements that are shown. We check both for
   * exact match and also variations.
   *
   * For the company tab, since the URL includes a `companyUuid`, it prevents a complete match
   * with the preset, so have an early check for this specific case.
   *
   * For the data room, we must check url on including /documents/data-room early, bc we have two cases
   * of urls parts:
   * 1. /documents
   * 2. /documents/data-room
   */
  updateShowElements(): void {
    if (this.currentPage.includes(CP_URLS.BASE)) {
      this.showElements = URL_PRESET[CP_URLS.BASE];
      return;
    }
    if (this.currentPage.includes(DATA_ROOM_URLS.ADMIN)) {
      this.showElements = URL_PRESET[DATA_ROOM_URLS.ADMIN];
      return;
    }
    Object.keys(URL_PRESET).forEach((key) => {
      const isEqual = key.replace('-', '_') === this.currentPage.replace('-', '_');
      const isContaining = this.currentPage.replace('-', '_').includes(key.replace('-', '_'));
      if (isEqual || (!isEqual && isContaining)) {
        this.showElements = URL_PRESET[key] as HorNavBarShowElements;
      }
    });
  }

  async getAllFP(): Promise<void> {
    if (!this.selectedOrganization || !this.selectedOrganization.selectedFund) {
      return;
    }
    if (![SOFTWARE_SERVICE.FMS, SOFTWARE_SERVICE.FS].includes(this.currentService)) {
      return;
    }
    const fps = await firstValueFrom(
      this.fundPerformanceService.getAllFundPerformancesByFundUuid(
        this.selectedOrganization.selectedFund.uuid, true, true));
    if (!fps) {
      return;
    }
    this.fundPerformances = fps;

    // because this method is async we need to wait until all fps are fetched and only then show modal update FP if needed
    if (this.is.fpUpdateAllowed) {
      this.is.showFpUpdateModal = true;
      this.is.fpUpdateAllowed = false;
    }
    this.setPeriodsByUserType(this.authService.getCurrentUserType());
  }

  /**
   * Updates in the event service selected org -> fund.
   *
   * @param fund is an Object of Fund.
   * @param organizationWithUserRole
   */
  async switchFund(fund: Fund, organizationWithUserRole: OrganizationWithAccesses): Promise<void> {
    const canProceed = await this.unsavedChangesService.checkCanProceed();
    if (!canProceed) {
      return;
    }

    const org = cloneDeep(organizationWithUserRole.organization);
    if (
      this.selectedOrganization?.selectedFund &&
      fund.id === this.selectedOrganization.selectedFund.id &&
      org.id === this.selectedOrganization.id
    ) {
      return;
    }
    this.resetPeriods();

    org.selectedFund = fund ? fund : null;
    this.organizationsService.setSelected(org);
    await this.router.navigate([], {
      queryParams: {
        f: fund?.uuid ? fund.uuid : '',
        o: org.uuid,
      },
    });
  }

  /**
   * Switches the current FundPerformance.
   *
   * This method updates the selected period in both {@link PeriodService}
   * and {@link HorNavbarService}, based on the provided {@link FundPerformance}.
   *
   * @param fp - The {@link FundPerformance} to switch to.
   *
   * @param isSavedPeriodToBeSelected - Optional. Indicates whether the provided `fp.period`
   *        is a previously saved period that should be explicitly selected.
   *        - If `true`, the method treats the period as preserved value after page navigation.
   *        - If `false` or `undefined`, the period is treated as a regular switch.
   */
  switchFp(fp: FundPerformance, isSavedPeriodToBeSelected?: boolean): void {
    if (!fp) {
      return;
    }
    this.isSavedPeriodToBeSelected = isSavedPeriodToBeSelected;
    this.periodService.set(String(fp.period));
    this.navbarService.setSelectedPeriod(fp.period);
  }

  /**
   * Resets periods e.x. after the org/fund was changed.
   */
  resetPeriods(): void {
    this.navbarService.setSelectedPeriod(null);
    this.navbarService.setPeriods(null);
    this.selectedPeriod = null;
    this.periods = [];
  }

  /**
   * Updates in the navbar service a number switcher state.
   * Switcher turns on pipe, which can show values: normal, k, kk.
   *
   * @param s is the number, how much values should be divided.
   * e.x. possible @param s values are 1, 1000, 1000000
   */
  setNumberSwitcher(s: SwitcherNumberType): void {
    this.navbarService.setNumberSwitcher(s);
  }

  /**
   * Updates in the navbar service a switcher state.
   * Switcher turns on pipe, which can show values: Day, Mon, Qtr.
   *
   * @param s is the string, with the values given before.
   * e.x. possible @param s values are day, mon, qtr
   */
  setDateSwitcher(s: SwitcherDateType): void {
    this.navbarService.setDateSwitcher(s);
  }

  changeViewerAdminView(): void {
    const typeAfterChange = this.userType === 'admin' ? 'viewer' : 'admin';
    this.authService.setCurrentUserType(typeAfterChange);
  }

  async setSelectedOrgFromOrganizationPage(value: Organization): Promise<void> {
    const canProceed = await this.unsavedChangesService.checkCanProceed();
    if (!canProceed) {
      return;
    }
    this.showPicker.org.isShown = false;

    const id = value.id;
    const index = this.organizations.findIndex((el) => el.id === id);
    let fund: Fund;
    if (index !== -1) {
      const org = this.organizations[index];
      this.selectedOrganization = org;
      fund = org.selectedFund;
      if (fund === null || fund === undefined) {
        if (org.funds?.length > 0) {
          fund = org.funds[0];
        } else {
          fund = null;
        }
      }
      this.organizationsService.setSelected(org);
      await this.router.navigate([], { queryParams: { f: fund?.uuid, o: org.uuid } });
    }
  }

  setFilteredOrganizations(): void {
    // After mouseEnter the input emits empty value, therefore we check previous value from formControl
    this.filteredOrganizations = this.selectedSingleOrgForm.controls.selectedSingleOrg
      .valueChanges.pipe(
        startWith(''),
        map((value: string) => this._filterSingleOrg(value || this.selectedSingleOrgForm.controls.selectedSingleOrg.value as string)),
        takeUntil(this._destroyStream),
      );
  }

  checkAccessAfterChange(orgWithUserRole: OrganizationWithAccesses): void {
    if (!orgWithUserRole) {
      return;
    }
    switch (this.currentService) {
      case SOFTWARE_SERVICE.FMS:
        this.setIsMultiAccess(orgWithUserRole.userAccesses.fmsAccess);
        break;
      case SOFTWARE_SERVICE.KPI:
        this.setIsMultiAccess(orgWithUserRole.userAccesses.kpiAccess);
        break;
      case SOFTWARE_SERVICE.MA:
        this.setIsMultiAccess(EmployeeRole.viewer);
        break;
      case SOFTWARE_SERVICE.FS:
        this.initFsSubscriptions();
        this.setIsMultiAccess(orgWithUserRole.userAccesses.financialStatementAccess);
        break;
      case SOFTWARE_SERVICE.UTIL_PAGE:
        this.setIsMultiAccess(orgWithUserRole.userAccesses.fmsAccess);
        break;
    }
  }

  checkAccessForCurrentService(orgWithUserRole: OrganizationWithAccesses): boolean {
    if (!orgWithUserRole) {
      return;
    }
    switch (this.currentService) {
      case SOFTWARE_SERVICE.FMS:
        return orgWithUserRole.userAccesses.fmsAccess !== EmployeeRole.noAccess;
      case SOFTWARE_SERVICE.KPI:
        return orgWithUserRole.userAccesses.kpiAccess !== EmployeeRole.noAccess;
      case SOFTWARE_SERVICE.MA:
        return orgWithUserRole.userAccesses.marketAnalysisAccess !== EmployeeRole.noAccess;
      case SOFTWARE_SERVICE.FS:
        return orgWithUserRole.userAccesses.financialStatementAccess !== EmployeeRole.noAccess;
      case SOFTWARE_SERVICE.DATA_ROOM:
        return orgWithUserRole.userAccesses.dataRoomAccess !== EmployeeRole.noAccess;
      case SOFTWARE_SERVICE.UTIL_PAGE:
        return orgWithUserRole.userAccesses.fmsAccess !== EmployeeRole.noAccess;
    }
  }

  activateTooltip(id: string): void {
    this.checkAndSetIsSwitcherDisabled();
    if (!this.is.switcherDisabled) {
      return;
    }
    this.toolTipSettings.title = PERIOD_IS_HIDDEN;
    this.toolTipSettings.show = true;
    this.toolTipSettings.coordinates = document.getElementById(id)?.getBoundingClientRect();
  }

  hideTooltip(): void {
    this.toolTipSettings.show = false;
    this.is.tooltipForSwitcher = false;
  }

  async blockPeriod(period: Date, value: boolean): Promise<void> {
    this.hideTooltip();
    const fundPerformance = await firstValueFrom(this.fundPerformanceService.blockPeriod(
      this.selectedOrganization.selectedFund.uuid, period, value,
    ).pipe(takeUntil(this._destroyStream)));
    this.navbarService.updateFundPerformance(fundPerformance);
    this.selectedFundPerformance.blocked = value;
  }

  pickerActionCatcher(actionFlag: PERIOD_PICKER_ACTION): void {
    switch (actionFlag) {
      case (PERIOD_PICKER_ACTION.COPY): {
        this.is.copyPeriodModalOpen = true;
        break;
      }
      case PERIOD_PICKER_ACTION.BLOCK: {
        void this.blockPeriod(this.selectedFundPerformance.period, !this.selectedFundPerformance?.blocked);
        break;
      }
      case PERIOD_PICKER_ACTION.DELETE: {
        this.is.deletePeriodModalOpen = true;
      }
    }

  }

  /**
   * Handles the copy period emitted event.
   *
   * There are 3 cases:
   * 1. User just closed the modal, without copying period => set is.copyPeriodModalOpen to false.
   * 2. User copied period without updating expenses => set new period as selected, reload all fund performances, close copy period modal.
   * 3. User copied period with updating expenses => set new period as selected, reload all fund performances
   * and open update fund performance modal with opening expenses prediction modal inside, close copy period modal.
   *
   * @param newPeriodWrapper - Wrapper object containing the new period and a flag that indicates whether to show the update fund performance modal.
   */
  copyPeriodEmitted(newPeriodWrapper: NewPeriodEmitWrapper): void {
    this.is.copyPeriodModalOpen = false;
    if (!newPeriodWrapper) {
      return;
    }
    this.hideTooltip();
    if (newPeriodWrapper.newPeriod) {
      this.periodService.set(String(newPeriodWrapper.newPeriod));
      this.isSavedPeriodToBeSelected = true;
      this.navbarService.fetchAllFundPerformancesFromBackend();
    }
    if (newPeriodWrapper.showUpdateFpModal) {
      this.is.fpUpdateAllowed = true;
    }
  }

  deletePeriodEmitted(value: boolean): void {
    this.hideTooltip();
    if (value) {
      this.navbarService.fetchAllFundPerformancesFromBackend();
    }
    this.is.deletePeriodModalOpen = false;
  }

  /**
   * By choosing the company:
   * 1) Sets the selected org and fund.
   * 2) Sends the trigger to the components to update the last selected period (the newest PP period)
   * 3) If the company is in the same fund, period will be the same. (period change will occur, but on the same as before).
   *
   * @param company to navigate to in the "Company Performance".
   */
  async changeFundAndPeriodByCompany(company: CompanySearchDto): Promise<void> {
    const org = this.organizationsWithRoles.find((o) => o.organization.uuid === company.org.uuid);
    const fund = this.organizations.find((o) => company.org.uuid === o.uuid).funds.find((f) => f.uuid === company.fund.uuid);
    const isSameFund = company.fund.id === this.selectedOrganization.selectedFund.id;
    const prevPeriod = this.selectedPeriod;
    await this.switchFund(fund, org);
    const period = this.fundPerformances.find((fp) =>
      (fp.period === (isSameFund ? prevPeriod : company.period)));
    this.switchFp(period);
    const url = `${CP_URLS.BASE}/${company.uuid}`;
    await this.router.navigate([url], { queryParams: this.route.snapshot.queryParams });
  }

  /**
   * Returns the user from box file picker folders to the initial page in the admin Data Room .
   *
   * When the back button, activated with the parameter `isDisableOrgSelectorAndShowButtonBack = true`, is pressed,
   * this method signals the `boxService` to return to the initialization page.
   * As a result, the display in the `hovNavBar` changes:
   * - The back button disappears.
   * - The organization picker becomes active instead of being blocked.
   */
  clickOnBack(): void {
    this.boxService.setIfOrganizationPickerIsBlocked(false);
  }

  /**
   * triggered in html by org picker emission,
   * triggers fs navigation if appropriate service,
   * updates fund globally if not management entity
   *
   * @param selectedEntityAndOrg to pass to switchFundOnClick
   *
   */
  changeSelectedEntity(selectedEntityAndOrg: SelectedEntityAndOrg): void {
    const { entity, org } = selectedEntityAndOrg;
    if (this.currentService === SOFTWARE_SERVICE.FS) {
      void this.fsNavigationService.changeSelectedOwnershipEntity(entity);
    }
    // update selected fund as usual if it's not a managementEntity which the rest of project is not meant to process
    const isManagementEntity = !('fundId' in entity);
    if (isManagementEntity) {
      this.is.selectedManagementEntity = true;
      return;
    }
    this.is.selectedManagementEntity = false;
    void this.switchFund(entity, org);
  }

  /**
   * Return user to the previous page
   */
  goBack(): void {
    if (this.router.url.includes(OrgUserManagementUrls.base)) {
      this.router.navigate([EntranceUrl], { queryParams: this.route.snapshot.queryParams });
      return;
    }
    this.location.back();
  }

  selectMarketAnalysisPeriod(period: LocalDate, selector: MaSelector): void {
    switch (selector) {
      case MaSelector.REPORTED_PERIODS:
        this.marketAnalysis.reported.selected = period;
        this.marketInternalService.sendSelectedReportedPeriod(period);
        break;
      case MaSelector.FROM:
        this.marketAnalysis.vintage.from.selected = period;
        this.marketInternalService.sendSelectedVintageFrom(period);
        break;
      case MaSelector.TILL:
        this.marketAnalysis.vintage.till.selected = period;
        this.marketInternalService.sendSelectedVintageTill(period);
        break;
    }
  }

  async goToAllPeriods(): Promise<void> {
    const url = FP_URLS.BASE + '/' + FP_URLS.VALUE_CREATION;
    await this.router.navigate([url], { queryParams: this.route.snapshot.queryParams });
  }

  openAiModal(): void {
    this.showGlobalAiModal = true;
  }

  /**
   * Updates the selected fund performance after a user submits changes via the "Update Fund Performance" modal.
   *
   * @param fundPerformance - The updated fund performance submitted from the modal.
   */
  updateFpFromModal(fundPerformance: FundPerformance): void {
    this.navbarService.updateFundPerformance(fundPerformance);
    this.navbarService.setSelectedFundPerformance(fundPerformance);
  }

  closeFpUpdateModal(value: boolean): void {
    if (value) {
      this.navbarService.fetchAllFundPerformancesFromBackend();
    }
    this.is.showFpUpdateModal = false;
  }

  async deleteFundPerformance(shouldDelete: boolean): Promise<void> {
    this.is.showDeleteFpModal = false;
    if (!shouldDelete) {
      return;
    }
    await firstValueFrom(this.fundPerformanceService.delete(
      this.selectedOrganization.selectedFund.uuid,
      this.selectedPeriod,
    ).pipe(takeUntil(this._destroyStream)));
    this.navbarService.fetchAllFundPerformancesFromBackend();
    this.notificationService.success(NOTIFICATIONS.FUNDPERFORMANCE_DELETE_SUCCESS);
    this.is.showFpUpdateModal = false;
  }

  private initSubscription(): void {
    // All organizations fetch
    this.organizationsService.all$
      .pipe(takeUntil(this._destroyStream))
      .subscribe((organizations) => {
        if (!organizations) {
          return;
        }
        this.organizations = organizations;

        this.setFilteredOrganizations();
        this.selectOrgAndFundFromQueryParam();
        this.resetOrgFundInputValue();

        if (organizations.length === 1 && organizations[0].funds.length) {
          const fund = this.selectedOrganization?.selectedFund ?? organizations[0].funds[0];
          this.switchFund(fund, this.organizationsWithRoles[0]);
        }

        this.showUIService.set(true);
      });

    // Subscribe on selected organization change.
    this.organizationsService.selected$
      .pipe(takeUntil(this._destroyStream))
      .subscribe((org) => {
        if (!org) {
          return;
        }
        this.isSavedPeriodToBeSelected = true;
        if (this.selectedOrganization) {
          this.isSavedPeriodToBeSelected = this.selectedOrganization && this.selectedOrganization.id === org.id;
        }
        this.sortOrgFundsBySpvStatusAndOrder(org);
        this.selectedOrganization = org;
        this.checkAccessAfterChange(this.organizationsWithRoles.find((o) => o.organization.id === org.id));
        this.getAllFP();
      });

    this.organizationsService.selectedWithAccesses$
      .pipe(takeUntil(this._destroyStream))
      .subscribe((org) => {
        if (!org) {
          return;
        }
        this.orgAccesses = this.organizationsService.selectedOrgAccesses;
        this.showUserManagementOptions = (this.orgAccesses.isSuperAdmin || this.orgAccesses.isOrganizationAdmin) ||
          org.userAccesses.hasAtLeastOneAccess(org);
      });

    // periods fetch
    this.navbarService.getPeriods
      .pipe(takeUntil(this._destroyStream))
      .subscribe((data) => {
        if (!data || data === this.periods) {
          return;
        }
        if (!data.length) {
          this.periods = [];
          return;
        }
        this.periods = data;
      });

    this.navbarService.getSelectedPeriod
      .pipe(takeUntil(this._destroyStream))
      .subscribe((period: Date) => {
        // 1970-1-1 uses as marker that we have no period
        if (!period || period?.toString() === '1970-01-01') {
          return;
        }
        this.selectedPeriod = period;
        this.selectFundPerformanceBySelectedPeriod(period);
        this.navbarService.setSelectedFundPerformance(this.selectedFundPerformance);
      });

    this.navbarService.getCustomShowElements
      .pipe(takeUntil(this._destroyStream))
      .subscribe((o: HorNavBarShowElements) => {
        if (o) {
          this.showElements = o;
        }
      });
    this.marketInternalService.reportedPeriodsState
      .pipe(takeUntil(this._destroyStream))
      .subscribe((marketPeriods) => {
        if (!marketPeriods) {
          return;
        }
        this.marketAnalysis.reported.periods = marketPeriods;
        this.marketAnalysis.reported.selected = marketPeriods[0];
        this.marketInternalService.sendSelectedReportedPeriod(marketPeriods[0]);
      });

    this.marketInternalService.vintagePeriodsState
      .pipe(takeUntil(this._destroyStream))
      .subscribe((vintagePeriods) => {
        if (!vintagePeriods) {
          return;
        }
        this.marketAnalysis.vintage.initial = vintagePeriods;
      });

    this.marketInternalService.selectedVintageTillState
      .pipe(takeUntil(this._destroyStream))
      .subscribe((tillPeriod) => {
        if (tillPeriod) {
          this.marketAnalysis.vintage.till.selected = tillPeriod;
          this.updateMaVintagePeriods();
        }
      });

    this.marketInternalService.selectedVintageFromState
      .pipe(takeUntil(this._destroyStream))
      .subscribe((fromPeriod) => {
        if (fromPeriod) {
          this.marketAnalysis.vintage.from.selected = fromPeriod;
          this.updateMaVintagePeriods();
        }
      });


    this.organizationsService.emulateSwitchFundState$.subscribe(
      (data) => {
        if (!data) {
          return;
        }
        this.changeRouteAndSwitchFund(data);
      });

    this.authService.currentUserTypeForServices$.pipe(takeUntil(this._destroyStream))
      .subscribe((userType) => {
        if (!userType) {
          return;
        }
        this.userType = userType;
      },
      );

    this.authService.currentUserTypeForServices$.pipe(takeUntil(this._destroyStream))
      .subscribe((userType) => {
        if (!userType || !this.fundPerformances) {
          return;
        }
        this.isSavedPeriodToBeSelected = true;
        this.setPeriodsByUserType(userType);
      });

    this.initService._initDataRefresher.asObservable()
      .pipe(takeUntil(this._destroyStream))
      .subscribe((refresh) => {
        if (refresh) {
          this.getInitialData();
        }
      });

    /**
     * After the fund performance component has changed
     * the visibility or blocked condition -> update it in the period list.
     */
    this.navbarService.getUpdateFundPerformance.pipe(takeUntil(this._destroyStream)).subscribe((fundPerformance) => {
      if (!fundPerformance) {
        return;
      }

      const index = this.fundPerformances?.findIndex((f) => f.uuid === fundPerformance.uuid);
      if (index !== -1) {
        this.fundPerformances[index] = cloneDeep(fundPerformance);
      }
      if (this.selectedFundPerformance.uuid === fundPerformance.uuid) {
        this.selectedFundPerformance = cloneDeep(fundPerformance);
        this.navbarService.setSelectedFundPerformance(fundPerformance);
      }
    });

    /**
     * Fetches all fund performances from backend
     * in case other component asked to update all periods.
     * In case a period is passed, the period is set after all fps are fetched.
     */
    this.navbarService.getFetchAllFundPerformancesFromBackend
      .pipe(
        filter((wrapper) => Boolean(wrapper)),
        tap((wrapper) => {
          if (wrapper.type === 'isSavedPeriodToBeSelected') {
            this.isSavedPeriodToBeSelected = true;
          }
        }),
        switchMap((wrapper) =>
          from(this.getAllFP())
            .pipe(
              tap(() => {
                if (wrapper.selectedPeriod) {
                  // @ts-expect-error date types
                  const selectedFp = this.fundPerformances.find((fp) => fp.period === wrapper.selectedPeriod);
                  if (selectedFp) {
                    void this.switchFp(selectedFp);
                  }
                }
              }),
            )),
        takeUntil(this._destroyStream),
      )
      .subscribe();

    // This is used for data room to control the display of the "Back" button and block the organization picker if necessary
    this.boxService.organizationPickerIsBlocked
      .pipe(takeUntil(this._destroyStream))
      .subscribe((isBlock) => {
        this.isDisableOrgSelectorAndShowButtonBack = isBlock;
      });
  }

  /**
   * trigger fsNavService data fetch and set to init all other fs data before subscribing;
   * key data is released from pipe after fs service exit
   * */
  private initFsSubscriptions(): void {
    if (!this.selectedOrganization) {
      return;
    }
    // calling fsNav entities and selectedEntity to trigger rest of fsNav setup
    void this.fsNavigationService.getEntities();

    void this.fsNavigationService.changeSelectedOwnershipEntity(this.validateSelectedEntityForFs());

    // subscribe to fs and check status for ui
    this.fsGetterSetter.financialStatement
      .pipe(takeUntil(this._fsDestroyStream))
      .subscribe((financialStatement: FinancialStatement): void => {
        this.fsNavData.isCompleted = financialStatement?.isCompleted;
      });
    this.fsGetterSetter.ownershipEntities
      .pipe(takeUntil(this._fsDestroyStream))
      .subscribe((entities: (Fund | ManagementEntity)[]): void => {
        if (!entities) {
          return;
        }
        this.fsNavData.entities = entities;
      });
    // subscribe to fs entity and trigger fs service ownership change and fs update
    this.fsGetterSetter.selectedOwnershipEntity
      .pipe(takeUntil(this._fsDestroyStream))
      .subscribe((selectedEntity: Fund | ManagementEntity): void => {
        if (selectedEntity) {
          this.fsNavData.selectedEntity = selectedEntity;
          this.is.selectedManagementEntity = this.fsService.ownershipEntityType === OwnershipEntityType.MANAGEMENT_ENTITY;
          return;
        }
        if (this.selectedOrganization.selectedFund) {
          void this.fsNavigationService.changeSelectedOwnershipEntity(this.selectedOrganization.selectedFund);
        }
      });
    this.fsGetterSetter.ownershipEntityPeriods
      .pipe(takeUntil(this._fsDestroyStream))
      .subscribe((fsPeriodsWithStatus: FsSelectorPeriod[]): void => {
        if (!this.periodPicker) {
          return;
        }
        this.periodPicker.fsNavData.periods = fsPeriodsWithStatus;
      });
    this.fsGetterSetter.selectedEntityPeriod
      .pipe(takeUntil(this._fsDestroyStream))
      .subscribe((selectedPeriod: FsSelectorPeriod): void => {
        this.fsNavData.selectedPeriod = selectedPeriod;
      });
  }

  private validateFsState(): void {
    if (this.currentService === SOFTWARE_SERVICE.FS) {
      if (this.fsService.ownershipEntityType === OwnershipEntityType.MANAGEMENT_ENTITY) {
        // if current service is FS and next is not, youre leaving FS and if last selected Entity wasnt fund,
        // select last selected fund
        this.orgPicker.selectedEntity = this.selectedOrganization.selectedFund;
      }
      // if current service is FS and next is not, so unsub from fs
      this.fsUnsubscribe();
    }
  }

  private showOrgDropDown: () => void = () => {
    this.showPicker.org.isShown = true;
  };

  private hideOrgDropDown: () => void = () => {
    this.showPicker.org.isShown = false;
  };

  private validateSelectedEntityForFs(): Fund|ManagementEntity {
    const savedManagementEntityId = FsGettersSettersService.getSavedFsOwnershipEntityId();
    let entity: Fund|ManagementEntity;
    if (savedManagementEntityId) {
      entity = this.selectedOrganization.managementEntities.find((orgEntity) => orgEntity.id === savedManagementEntityId);
    } else {
      entity = this.selectedOrganization.selectedFund;
    }
    return entity;
  }

  private fsUnsubscribe(): void {
    this.is.selectedManagementEntity = false;
    this.fsService.ownershipEntityType = OwnershipEntityType.FUND;
    FsGettersSettersService.setSavedFsOwnershipEntityId(null);
    this._fsDestroyStream.next();
    this._fsDestroyStream.complete();
  }

  private changeRouteAndSwitchFund(data: ChangeRouteAndSwitchFund): void {
    const { fund, orgUuid, fp, route } = data;
    const org = this.organizationsWithRoles.find((o) => o.organization.uuid === orgUuid);

    this.switchFund(fund, org).then(async () => {
      this.switchFp(fp, true);
      await firstValueFrom(this.navigationService.navigatePreservingScroll(route, false, { f: data.fund.uuid, o: orgUuid }));
    });
  }

  private setIsMultiAccess(access: EmployeeRole): void {
    this.isMultiAccess = access.toString().toLowerCase().includes('admin');
    if (!this.isMultiAccess && this.userType === 'admin') {
      this.changeViewerAdminView();
    }
  }

  private defineCurrentPageAndService(): void {
    this.currentPage = this.parseCurrentPageURL(this.router.url);
    this.currentPageHeader = URLToHeaderName.getHeaderName(this.currentPage);
    let urlHeader = this.currentPage.split('/')[0];
    if (this.currentPage.includes('data-room')) { // temporary kostIbL coz url for data room now looks documents/data-room
      urlHeader = this.currentPage.split('/')[1];
    }

    const nextService = URLToHeaderName.getServiceName(urlHeader);
    if (nextService && (!this.currentService || this.currentService !== nextService)) {
      this.validateFsState();
      this.currentService = nextService;
      if ([SOFTWARE_SERVICE.FMS, SOFTWARE_SERVICE.FS].includes(this.currentService)) {
        this.getAllFP();
      }
      this.updateOrgsWithRoles();
      // this portion only makes sense in entrance?
      if (this.selectedOrganization) {
        this.checkAccessAfterChange(
          this.organizationsWithRoles
            .find((o) => o.organization.id === this.selectedOrganization.id));
      }
    }
  }

  /**
   * ToDo: will be refactored by putting in the outer service. Do not forget to update regexp service (pr awaiting for 2.0)
   * <p>
   * Provides the clean url without parameters and uuids.
   * This is used e.x. by hornavbar to show fp icons depending on the current page.
   * <p>
   * Precondition: Expects to start with a slash after the domain.
   * <pre>
   * Example:
   * Given the   "/portfolios/company/458a479d-cda8-44d8-93da-93d539d0cdb4?f=8cf928b0-db54-4cae-976d-f56c34309817"
   * Will return "portfolios/company"
   * </pre>
   *
   * @param url - can be retrieved by `this.router.url`.
   * @returns the url without parameters and uuids.
   */
  private parseCurrentPageURL(url: string): string {
    const urlWithoutParams = url.substring(1).split('?')[0];
    const lastSlashPart = urlWithoutParams.split('/').at(-1);
    // if the last url part is uuid, remove it. E.x.: portfolio/company/458a479d-cda8-44d8... to portfolio/company
    if (new RegExpService().uuidV4.test(lastSlashPart)) {
      return urlWithoutParams.substring(0, urlWithoutParams.lastIndexOf('/'));
    }
    return urlWithoutParams;
  }

  private _filterSingleOrg(value: string): Organization[] {
    const filterValue = value.toLowerCase();
    const orgsForFilter = this.organizations.filter((org) =>
      this.organizationsWithServiceAccess.some((accessOrg) =>
        accessOrg.organization.uuid === org.uuid));
    return orgsForFilter.filter((el) =>
      el.name.toLowerCase().includes(filterValue),
    );
  }

  private setPeriodsByUserType(userType: UserType): void {
    this.filteredFundPerformances = this.fundPerformances;
    switch (userType) {
      case 'admin':
        break;
      case 'viewer':
        this.filteredFundPerformances = this.fundPerformances.filter((_fp) => _fp.isVisible);
        break;
    }
    // send to other components the periods.
    const filtered = this.filteredFundPerformances.map((_fp) => _fp.period);
    this.navbarService.setPeriods(filtered);
    this.navbarService.setFundPerformances(this.filteredFundPerformances);
    if (!this.filteredFundPerformances?.length) {
      // 1970-1-1 uses as marker that we have empty FundPerformance[] and no period
      // to control subscription and understand exactly a meaning value of subscription
      // todo will be refactored in https://app.clickup.com/t/86bynzddv
      // @ts-expect-error date types
      this.navbarService.setSelectedPeriod('1970-01-01');
      return;
    }

    const periodFromLocalStorage = this.periodService.get();
    const fp = this.filteredFundPerformances.find((f) =>
      f.period.toString() === periodFromLocalStorage);
    // no further action needed if fp is the same
    if (fp && fp === this.selectedFundPerformance) {
      return;
    }
    // switch fp if saved period was provided and corresponding fp was found in fps
    if (fp && this.isSavedPeriodToBeSelected) {
      void this.switchFp(fp);
      return;
    }
    // fs has its own logic: no need to call this.selectDefaultFundPerformance()
    if (this.currentService === SOFTWARE_SERVICE.FS) {
      return;
    }
    this.switchFp(this.selectDefaultFundPerformance());
  }

  /**
   * Selects the default fund performance:
   * 1. If there are 2 quarters after today, it selects the latest period in the quarters.
   * e.x. today is 2024-09-30, it will choose latest period from 2024-09-30 till 2025-03-31.
   * 2. If there is no such quarter, it selects the first fund performance before (or) today.
   * e.x. today is 2024-10-15 will choose 2024-09-30.
   *
   * @returns The selected fund performance object, or null if no performances are available.
   */
  private selectDefaultFundPerformance(): FundPerformance {
    // Return null if no fund performances are available
    if (!this.filteredFundPerformances || this.filteredFundPerformances.length === 0) {
      return null;
    }

    // Calculate the date for the start of the next quarter
    const nextQuarterDate = new Date();
    nextQuarterDate.setMonth(nextQuarterDate.getMonth() + 7, 1);
    const nextQuarterDateString = DateService.toHyphenDate(nextQuarterDate);

    const performancesBeforeNextQuarter = this.filteredFundPerformances
      .filter((fundPerformance) => fundPerformance.period.toString() < nextQuarterDateString);

    if (performancesBeforeNextQuarter.length > 0) {
      return performancesBeforeNextQuarter[0];
    }
    return this.filteredFundPerformances[0];
  }

  private selectOrgAndFundFromQueryParam(): void {
    // parse org and fund to set it after refresh/new page
    const params = this.route.snapshot.queryParams;
    const { o: orgUuid, f: fundUuid } = params as { o?: string; f?: string };
    if (!orgUuid) {
      return;
    }
    const selectedOrg = this.organizations.find((o) => o.uuid === orgUuid);
    if (!selectedOrg) {
      return;
    }
    selectedOrg.selectedFund = selectedOrg.funds.find((f) => f.uuid === fundUuid);
    if (!selectedOrg.selectedFund) {
      selectedOrg.selectedFund = selectedOrg.funds.length ? selectedOrg.funds[0] : null;
    }
    if (this.showElements.periods) {
      this.periodPicker.selectedOrganization = selectedOrg;
    }
    this.organizationsService.setSelected(selectedOrg);
  }

  private async getInitialData(): Promise<void> {
    const { organizations, profile, userAccess } = await firstValueFrom(this.initService.getInitialData());
    this.cabinetService.setProfile(profile);
    this.setUserInfo(profile, userAccess);
    this.organizationsWithRoles = organizations;
    this.updateOrgsWithRoles();
    this.organizationsService.setAll(organizations);
  }

  private updateOrgsWithRoles(): void {
    if (!this.organizationsWithRoles) {
      return;
    }
    const organizationsWithServiceAccess = this.organizationsWithRoles.filter((orgWithAccess) =>
      this.checkAccessForCurrentService(orgWithAccess));
    organizationsWithServiceAccess.forEach((orgWithAccess) => {
      this.sortOrgFundsBySpvStatusAndOrder(orgWithAccess.organization);
    });
    this.organizationsWithServiceAccess = organizationsWithServiceAccess;
  }

  private setUserInfo(profile: Profile, userAccess: InitDataUserAccess): void {
    const authByCookie = this.cookie.get('user_info');
    const splitAuthCookie = authByCookie.split('&');
    const userId = Number(splitAuthCookie[0]);
    const userMail = splitAuthCookie[1];
    this.authService.setUserInfo({
      id: userId,
      mail: userMail,
      name: profile.name,
      userRole: userAccess.userRole,
      portalRole: userAccess.portalRole,
    });
  }

  private selectFundPerformanceBySelectedPeriod(period: Date): void {
    this.selectedFundPerformance =
      this.fundPerformances?.find((fund) => fund.period === period);
  }

  private subscribeToScroll(): void {
    this.ngZone.runOutsideAngular(() => {
      fromEvent(window, 'scroll')
        .pipe(takeUntil(this._destroyStream), distinctUntilChanged(), debounceTime(100))
        .subscribe(() => this.checkIfScrolled());
    });
  }

  private checkAndSetIsSwitcherDisabled(): void {
    if (this.currentService === SOFTWARE_SERVICE.FS) {
      this.is.tooltipForSwitcher = true;
      this.is.switcherDisabled = false;
      return;
    }
    this.is.tooltipForSwitcher = true;
    this.is.switcherDisabled = this.selectedFundPerformance && !this.selectedFundPerformance.isVisible && this.userType === 'admin';
  }

  private checkIfScrolled(): void {
    const currentScroll = window.scrollY > SCROLL_THRESHOLD_PX;
    if (currentScroll !== this.hasScrolledDown) {
      this.ngZone.run(() => {
        this.hasScrolledDown = currentScroll;
      });
    }
  }

  /**
   * Filter MA Vintage periods based on selected values.
   *
   * FromPeriod should not be later than TillPeriod.
   * TillPeriod should not be earlier than FromPeriod.
   */
  private updateMaVintagePeriods(): void {
    const fromPeriod = this.marketAnalysis.vintage.from.selected;
    const tillPeriod = this.marketAnalysis.vintage.till.selected;
    const allPeriods = cloneDeep(this.marketAnalysis.vintage.initial);

    this.marketAnalysis.vintage.from.periods = allPeriods.filter((period) => period <= tillPeriod);
    this.marketAnalysis.vintage.till.periods = tillPeriod ? allPeriods.filter((period) => period >= fromPeriod) : allPeriods;
  }

  /**
   * Sorts the funds in the provided organization by their SPV (Special Purpose Vehicle) status and order.
   *
   * The organization has funds, and users can change the order of these funds in the `FundTableComponent`.
   * Funds are divided into two groups: SPV funds and normal funds (non-SPV). The sorting happens within
   * these groups:
   *  - All SPV funds are grouped together, and normal (non-SPV) funds are grouped together.
   *  - Within each group, the funds are sorted by the `order.inOrgOverviewTable` field, which is a user-defined
   *    order set via the {@link FundTableComponent}.
   *
   * Examples:
   * 1. If two funds `A` (SPV) and `B` (normal fund) exist, `B` will appear before `A` because normal funds
   *    are sorted before SPV funds.
   * 2. If both `C` and `D` are normal funds, and `C.order.inOrgOverviewTable = 0` and `D.order.inOrgOverviewTable = 1`,
   *    `C` will be sorted before `D`.
   *
   * The resulting order is based first on SPV status, then on the defined order within each group.
   */
  private sortOrgFundsBySpvStatusAndOrder(org: Organization): void {
    if (!org) {
      return;
    }

    org.funds.sort((f1, f2) => {
      if (f1.spv !== f2.spv) {
        return f1.spv ? 1 : -1;
      }
      return f1.order.inOrgOverviewTable - f2.order.inOrgOverviewTable;
    });
  }
}

export interface ChangeRouteAndSwitchFund {
  fund?: Fund;
  orgUuid?: string;
  fp?: FundPerformance;
  fundUrl?: string;
  route?: string;
}

const SCROLL_THRESHOLD_PX = 10;

export class IsHorNav {
  switcherDisabled = false;
  tooltipForSwitcher = false;
  copyPeriodModalOpen = false;
  deletePeriodModalOpen = false;
  selectedManagementEntity = false;
  isActiveRemindersLoaded = false;
  showRemindersModal = false;
  showFpUpdateModal = false;
  showDeleteFpModal = false;
  fpUpdateAllowed = false;
}

const PERIOD_IS_HIDDEN = 'Period is hidden for viewer';

export enum SOFTWARE_SERVICE {
  FMS = 'FMS',
  FS = 'FS',
  KPI = 'KPI',
  MA = 'MA',
  DATA_ROOM = 'DATA_ROOM',
  UTIL_PAGE = 'UTIL_PAGE',
}
